import { Pagination, Select, Table, Form, Button } from 'antd';
import { CheckOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { checkPermission } from '../utils/checkPermission';
import ModalConfirm from './ModalConfirm';
import { BANNER_STATUS_CHANGE, POPUP_STATUS_CHANGE } from '../constants/apiEndPoint';

const { Option } = Select;

export const CheckTableCustom = ({
  isLoading,
  columns,
  dataSource,
  totalItem,
  params,
  setParams,
  handleApprove,
  roles,
  ...props
}) => {
  const [pageSize, setPageSize] = useState(params?.limit || 20);
  const [currentPage, setCurrentPage] = useState(params?.pageId || 1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState(null);

  // Sync component state with params
  useEffect(() => {
    setCurrentPage(params?.pageId || 1);
    setPageSize(params?.limit || 20);
  }, [params?.pageId, params?.limit]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRowKeys([]);
    setParams({
      ...params,
      pageId: page,
    });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setParams({
      ...params,
      limit: size,
      pageId: 1, // Reset to first page when changing page size
    });
    setCurrentPage(1);
  };

  const toggleConfirmOpen = (check, action) => {
    setConfirmOpen(check);
    if (action) {
      setActionType(action);
    }
  };

  const checkPermissionRole = () => {
    if (roles === 'popups') {
      return checkPermission(POPUP_STATUS_CHANGE, 'Duyệt');
    } else if (roles === 'banners') {
      return checkPermission(BANNER_STATUS_CHANGE, 'Duyệt');
    }
    return false;
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    onSelect: (record, selected) => {
      setSelectedRowKeys((prevSelectedKeys) => {
        if (selected) {
          return [...prevSelectedKeys, record.id];
        } else {
          return prevSelectedKeys.filter((key) => key !== record.id);
        }
      });
    },
    onSelectAll: (selected, selectedRows) => {
      if (selected) {
        setSelectedRowKeys(selectedRows.map((row) => row.id));
      } else {
        setSelectedRowKeys([]);
      }
    },
  };

  return (
    <>
      {confirmOpen && (
        <ModalConfirm
          Confirm={toggleConfirmOpen}
          handleApprove={(data) => {
            handleApprove(data);
            setSelectedRowKeys([]);
          }}
          selectedRowKeys={selectedRowKeys?.filter((item) =>
            dataSource?.find(
              (itemBanner) => itemBanner?.id === item && itemBanner?.status === (actionType === 'approve' ? 0 : 1)
            )
          )}
          actionType={actionType}
          roles={roles}
        />
      )}
      <Form>
        <Form.Item>
          {selectedRowKeys.length > 0 && (
            <div className="flex justify-start mb-4">
              <div className="mr-5">
                {checkPermissionRole(roles) && (
                  <Button
                    color="primary"
                    variant="filled"
                    className="h-[40px]"
                    onClick={() => toggleConfirmOpen(true, 'approve')}
                  >
                    <CheckOutlined className="text-blue-500 font-semibold text-[14px]" />
                    <span className="text-blue-500 font-semibold text-[14px]">Duyệt</span>
                  </Button>
                )}
              </div>
              <div>
                {checkPermissionRole(roles) && (
                  <Button
                    color="danger"
                    variant="filled"
                    className="h-[40px]"
                    onClick={() => toggleConfirmOpen(true, 'disapprove')}
                  >
                    <MinusCircleOutlined className="text-red-500 font-semibold text-[14px]" />
                    <span className="text-red-500 font-semibold text-[14px]">Chờ duyệt</span>
                  </Button>
                )}
              </div>
            </div>
          )}
        </Form.Item>
        <Form.Item>
          <Table
            {...props}
            loading={isLoading}
            rowKey={(record) => record.id} // Ensure each row has a unique key
            rowSelection={{
              type: 'checkbox',
              ...rowSelection,
            }}
            columns={columns}
            dataSource={dataSource?.map((item) => ({
              ...item,
              key: item.id, // Assign id to key
            }))}
            pagination={false}
            scroll={{
              x: 'max-content',
            }}
            footer={() =>
              dataSource?.length > 0 ? (
                <div className="flex flex-row justify-between">
                  <Pagination
                    pageSize={pageSize}
                    current={currentPage}
                    total={totalItem}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    itemRender={(current, type, originalElement) => {
                      if (type === 'prev') {
                        return <CaretLeftOutlined />;
                      }
                      if (type === 'next') {
                        return <CaretRightOutlined />;
                      }
                      return originalElement;
                    }}
                  />
                  <div className="flex flex-row justify-end items-center gap-2">
                    <Select value={pageSize} onChange={handlePageSizeChange}>
                      <Option value={10}>10</Option>
                      <Option value={20}>20</Option>
                      <Option value={50}>50</Option>
                    </Select>
                    <div className="text-sm text-[#667085] whitespace-nowrap">
                      Hiển thị từ {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalItem)} của{' '}
                      {totalItem}
                    </div>
                  </div>
                </div>
              ) : null
            }
            bordered
            // rowClassName={(record, index) =>
            //   index % 2 === 0 ? 'bg-gray-100 hover:bg-gray-200' : 'bg-white hover:bg-gray-200'
            // }
            // className="bg-white border rounded-lg"
          />
        </Form.Item>
      </Form>
    </>
  );
};

CheckTableCustom.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  columns: PropTypes.array.isRequired,
  dataSource: PropTypes.array.isRequired,
  totalItem: PropTypes.number.isRequired,
  params: PropTypes.object.isRequired,
  setParams: PropTypes.func.isRequired,
  handleApprove: PropTypes.func.isRequired,
  roles: PropTypes.string,
};
